import React from "react";

// icons
import { Groups as AccountsIcon } from "@mui/icons-material";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { BtcIconNoProps } from "./icons/BtcIcon";
import ChartsIcon from "./icons/ChartsIcon";
import DashboardIcon from "./icons/DashboardIcon";
import FiatDepositNoBackground from "./icons/FiatDepositNoBackground";
import MarketsIcon from "./icons/MarketsIcon";
import MarketsNavIcon from "./icons/MarketsNavIcon";
import TradeNavitionIcon from "./icons/TradeNavitionIcon";
import TransactionIcon from "./icons/TransactionIcon";
import TrustIcon from "./icons/TrustIcon";
import WalletsV2Icon from "./icons/WalletsV2Icon";
import DepositIcon from "./icons/navbar/DepositIcon";
import WithdrawIcon from "./icons/navbar/WithdrawIcon";

// pages
import { LandingPage } from "./features/landing-page/LandingPage";
import { TradePage } from "./features/trade/TradePage";
import AccountActivation from "./pages/AccountActivation";
import AccountSelection from "./pages/AccountSelection";
import AdhocKyc from "./pages/AdhocKyc";
import Addresses from "./pages/Addresses";
import AfterSignUpView from "./pages/AfterSignUp";
import Application from "./pages/Application";
import BankingDetails from "./pages/BankingDetails";
import Coin from "./pages/Coin";
import CryptoNews from "./pages/CryptoNews";
import Dca from "./pages/Dca";
import MaintenancePage from "./pages/MaintenancePage";
import Markets from "./pages/Markets";
import PasswordReset from "./pages/PasswordReset";
import PasswordResetError from "./pages/PasswordResetError";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordResetSuccess from "./pages/PasswordResetSuccess";
import Portfolio from "./pages/Portfolio";
import RPAnalyticsContainer from "./pages/RPAnalyticsContainer";
import RaF from "./pages/RaF";
import TravelRuleDone from "./pages/TravelRuleDone";
import ReKycApplication from "./pages/ReKycApplication";
import Research from "./pages/Research";
import SecureBiometrics from "./pages/SecureBiometrics";
import SecureFileUpload from "./pages/SecureFIleUpload";
import UKPublicAssessment from "./pages/UKPublicAssessment";
import Settings from "./pages/Settings";
import Sumsub from "./pages/Sumsub";
import { TargetOrders } from "./pages/TargetOrders";
import TaxCenter from "./pages/TaxCenter";
import TaxCenterSettings from "./pages/TaxCenterSettings";
import TaxCenterTransactions from "./pages/TaxCenterTransactions";
import TradeV2 from "./pages/TradeV2";
import Transactions from "./pages/Transactions";
import TransferDetail from "./pages/TransferDetail";
import Treemap from "./pages/Treemap";
import VerifyEmail from "./pages/VerifyEmail";
import Wallets from "./pages/Wallet";

// interface
import { Payment } from "./components/settings/payment/Payment";
import BusinessCompanyIcon from "./icons/CompanyIcon";
import RouteItem from "./model/RouteItem.model";
import Staking from "./pages/Staking";

// Lazy load dev pages to avoid circular dependency issues with store
// const DisclosureDevPage = React.lazy(() => import("./pages/dev/DisclosureDevPage"));

// define app routes
// Currently pointing all under development routes to Dashboard
// enabled = false will hide path from App Menu but path will still active
export const mainRoutes: RouteItem[] = [
  {
    title: "Portfolio",
    tooltip: "Portfolio",
    path: "/portfolio",
    menuItem: true,
    component: Portfolio,
    icon: DashboardIcon,
  },
  {
    title: "Settings",
    tooltip: "Settings",
    path: "/settings",
    menuItem: false,
    component: Settings,
    icon: Settings,
  },
  {
    title: "Profile",
    tooltip: "Profile",
    path: "/settings/profile",
    menuItem: false,
    component: Settings,
  },
  {
    title: "Referral",
    tooltip: "Referral Code Entry",
    path: "/settings/referral",
    menuItem: false,
    component: Settings,
    permissions: ["admin", "user", "developer", "beta"],
  },
  {
    title: "Security",
    tooltip: "Security",
    path: "/settings/security",
    menuItem: false,
    component: Settings,
  },
  {
    title: "Payment",
    tooltip: "Payment",
    path: "/settings/payment",
    menuItem: false,
    component: Settings,
    permissions: ["admin", "user", "developer", "beta"],
  },
  {
    title: "Useful Links",
    tooltip: "Useful Links",
    path: "/settings/usefulLinks",
    menuItem: false,
    component: Settings,
  },
  {
    title: "Change Password",
    tooltip: "Change Password",
    path: "/settings/changePassword",
    menuItem: false,
    component: Settings,
  },
  {
    title: "Onfido",
    tooltip: "Onfido",
    path: "/settings/biometrics",
    menuItem: false,
    component: Settings,
    permissions: ["guest", "admin", "beta"],
  },
  {
    title: "login",
    tooltip: "Login",
    path: "/",
    menuItem: false,
    component: LandingPage,
    icon: DashboardIcon,
    public: true,
  },
  {
    title: "Holdings",
    tooltip: "Holdings",
    path: "/wallets",
    menuItem: true,
    icon: WalletsV2Icon,
    component: Wallets,
  },
  {
    title: "TradeV2",
    tooltip: "TradeV2",
    menuItem: true,
    path: "/tradeV2",
    icon: TradeNavitionIcon,
    component: TradePage,
    featurePermission: ["trade-v2"],
    permissions: ["user", "developer", "admin", "guest", "beta"],
  },

  // Trade routes - OLD
  {
    title: "Trade",
    tooltip: "Trade",
    menuItem: true,
    path: "/trade",
    icon: TradeNavitionIcon,
    component: TradeV2,
    permissions: ["user", "developer", "admin", "guest", "beta"],
    subRoutes: [
      {
        title: "Swap",
        path: "/trade",
      },
      {
        title: "Targets",
        path: "/targets",
      },
      {
        title: "Scheduled orders",
        path: "/dca",
        new: true,
      },
    ],
  },
  {
    title: "Targets",
    tooltip: "Targets",
    menuItem: false,
    path: "/targets",
    icon: TradeNavitionIcon,
    component: TargetOrders,
    permissions: ["admin", "user", "developer", "guest", "beta"],
  },
  {
    title: "Scheduled orders",
    tooltip: "Scheduled orders",
    menuItem: false,
    path: "/dca",
    component: Dca,
    permissions: ["user", "developer", "admin", "beta", "guest"],
  },

  // Deposit routes
  {
    title: "Deposit",
    tooltip: "Deposit",
    menuItem: true,
    path: "/wallets/deposit",
    icon: DepositIcon,
    component: Wallets,
    subRoutes: [
      {
        title: "Deposit crypto",
        path: "/wallets/deposit",
      },
      {
        title: "Deposit fiat",
        path: "/wallets/details",
      },
    ],
  },
  {
    title: "Deposit crypto",
    menuItem: false,
    path: "/wallets/deposit",
    icon: BtcIconNoProps,
    component: Wallets,
  },
  {
    title: "Deposit Fiat",
    menuItem: false,
    path: "/wallets/details",
    icon: FiatDepositNoBackground,
    component: BankingDetails,
  },

  // Withdraw routes
  {
    title: "Withdraw",
    tooltip: "Withdraw",
    menuItem: true,
    path: "/wallets/addresses",
    icon: WithdrawIcon,
    component: Addresses,
    permissions: ["user", "developer", "admin", "guest", "beta"],
    subRoutes: [
      {
        title: "Withdraw Crypto",
        path: "/wallets/cryptoWithdraw",
      },
      {
        title: "Withdraw Fiat",
        path: "/wallets/fiatWithdraw",
      },
      {
        title: "Crypto addresses",
        path: "/wallets/addresses",
      },
      {
        title: "Bank accounts",
        path: "/bank-accounts",
      },
    ],
  },
  {
    title: "Withdraw crypto",
    menuItem: false,
    path: "/wallets/cryptoWithdraw",
    component: Wallets,
  },
  {
    title: "Withdraw fiat",
    menuItem: false,
    path: "/wallets/fiatWithdraw",
    component: Wallets,
  },
  {
    title: "Addresses",
    menuItem: false,
    path: "/wallets/addresses",
    component: Addresses,
  },
  {
    title: "Bank accounts",
    menuItem: false,
    path: "/bank-accounts",
    component: Payment,
  },
  // {
  //   title: "Fiat withdrawal",
  //   tooltip: "Fiat withdrawal",
  //   menuItem: true,
  //   path: "/wallets/fiatWithdraw",
  //   icon: TransactionIcon,
  //   component: Wallets,
  //   permissions: ["admin", "developer", "beta", "user"],
  // },
  // Market routes
  {
    title: "Markets",
    tooltip: "Markets",
    menuItem: true,
    path: "/markets",
    icon: MarketsNavIcon,
    component: Markets,
    subRoutes: [
      {
        title: "Cryptocurrencies",
        path: "/markets",
      },
      {
        title: "Crypto heatmap",
        path: "/markets/token-map",
      },
    ],
  },
  {
    title: "History",
    tooltip: "History",
    menuItem: true,
    path: "/transactions/overview",
    icon: TransactionIcon,
    component: Transactions,
  },
  {
    title: "History Report",
    tooltip: "History Report",
    menuItem: false,
    path: "/transactions/reports",
    icon: TransactionIcon,
    component: Transactions,
  },

  // D6/D7/D18 — Travel Rule transfer detail page. Shared across outbound +
  // inbound; sub-view selection is driven by the transfer's status.
  {
    title: "Transfer detail",
    tooltip: "Transfer detail",
    menuItem: false,
    path: "/transfers/:id",
    icon: TransactionIcon,
    component: TransferDetail,
  },

  // Refer a friend route
  {
    title: "Refer a friend",
    tooltip: "Refer a friend",
    menuItem: true,
    new: false,
    path: "/refer-a-friend",
    icon: TrustIcon,
    component: RaF,
  },

  // Referral Partner routes
  {
    title: "Referral partner analytics",
    tooltip: "Referral partner analytics",
    menuItem: true,
    new: true,
    path: "/referral-partner/analytics",
    icon: ChartsIcon,
    component: RPAnalyticsContainer,
    permissions: ["admin", "developer", "user", "beta"],
    featurePermission: ["rp-analytics"],
  },

  // Research route
  {
    title: "Research",
    tooltip: "Research",
    path: "/research",
    menuItem: true,
    icon: () => <SearchIcon style={{ fontSize: "20px" }} />,
    component: Research,
    new: true,
  },

  {
    title: "Tax Center",
    tooltip: "Tax Center",
    path: "/tax-center",
    menuItem: true,
    icon: () => <ReceiptLongOutlinedIcon style={{ fontSize: "20px" }} />,
    component: TaxCenter,
    featurePermission: ["tax-center"],
    new: true,
    subRoutes: [
      {
        title: "Overview",
        path: "/tax-center",
      },
      {
        title: "Transaction review",
        path: "/tax-center/transactions",
      },
    ],
  },

  {
    title: "Transaction review",
    tooltip: "Transaction review",
    path: "/tax-center/transactions",
    menuItem: false,
    component: TaxCenterTransactions,
    featurePermission: ["tax-center"],
  },

  {
    title: "Tax settings",
    tooltip: "Tax settings",
    path: "/tax-center/settings",
    menuItem: false,
    component: TaxCenterSettings,
    featurePermission: ["tax-center"],
  },

  // Crypto news route
  {
    title: "Crypto news",
    tooltip: "Crypto news",
    menuItem: false,
    path: "/crypto-news",
    icon: MarketsIcon,
    component: CryptoNews,
  },

  // Crypto coin page route
  {
    title: "Coin",
    tooltip: "Coin",
    menuItem: false,
    path: `/coin/:coin`,
    icon: MarketsIcon,
    component: Coin,
  },
  {
    title: "banking-details",
    tooltip: "Banking details",
    menuItem: false,
    path: "/banking/details",
    icon: MarketsIcon,
    component: BankingDetails,
  },
  {
    title: "Token map",
    tooltip: "Token map",
    menuItem: false,
    path: "/markets/token-map",
    icon: ChartsIcon,
    component: Treemap,
  },

  // Password reset routes
  {
    title: "Password Reset",
    menuItem: false,
    path: "/password/reset",
    icon: MarketsIcon,
    component: PasswordReset,
    public: true,
  },
  {
    title: "Password Reset Request",
    menuItem: false,
    path: "/reset/request",
    icon: MarketsIcon,
    component: PasswordResetRequest,
    public: true,
  },
  {
    title: "PasswordResetSuccess",
    public: true,
    menuItem: false,
    path: "/reset/request/success",
    icon: MarketsIcon,
    component: PasswordResetSuccess,
  },
  {
    title: "PasswordResetError",
    public: true,
    menuItem: false,
    path: "/reset/request/error",
    icon: MarketsIcon,
    component: PasswordResetError,
  },

  // Manual (no-portal) Travel Rule link — where Notabene's hosted SafeConnect
  // page sends the customer after they submit or cancel. Must stay PUBLIC:
  // these customers arrive from an emailed link with no portal session.
  {
    title: "TravelRuleDone",
    public: true,
    menuItem: false,
    path: "/travel-rule/done",
    icon: MarketsIcon,
    component: TravelRuleDone,
  },

  // Account activation route
  {
    title: "Account Activation",
    menuItem: false,
    path: "/account/activation",
    icon: MarketsIcon,
    component: AccountActivation,
    public: true,
  },

  // Legacy purpose
  {
    title: "Account Verification",
    menuItem: false,
    path: "/account/verification",
    icon: MarketsIcon,
    component: LandingPage,
    public: true,
  },

  // New page users will be redirected to after signing up
  {
    title: "after-signup",
    path: "/after-signup",
    menuItem: false,
    component: AfterSignUpView,
    icon: DashboardIcon,
    public: true,
  },
  {
    title: "account-selection",
    path: "/accounts",
    menuItem: false,
    component: AccountSelection,
    icon: DashboardIcon,
    public: false,
  },

  // Maintenance page route
  {
    title: "Maintenance Page",
    public: true,
    menuItem: false,
    path: "/maintenance",
    icon: MarketsIcon,
    component: MaintenancePage,
  },
  {
    // Route to the Signup Form Page
    title: "Signup",
    public: true,
    menuItem: false,
    path: "/SignUp",
    icon: MarketsIcon,
    component: LandingPage,
  },
  {
    title: "Secure File Upload",
    public: true,
    menuItem: false,
    path: "/secure-file-upload",
    icon: MarketsIcon,
    component: SecureFileUpload,
  },
  {
    title: "Secure Biometrics",
    public: true,
    menuItem: false,
    path: "/secure-biometrics",
    icon: MarketsIcon,
    component: SecureBiometrics,
  },
  // Adhoc Re-KYC (AU/NZ) magic-link landing. Must stay PUBLIC with no
  // `permissions`: customers arrive from an emailed link with no portal
  // session, and a non-public route would send them to the login page.
  // Logged-in customers reach the same page from the AdhocKycBanner.
  {
    title: "Identity Re-verification",
    public: true,
    menuItem: false,
    path: "/adhocKyc",
    icon: MarketsIcon,
    component: AdhocKyc,
  },
  {
    title: "UK Appropriateness Assessment",
    public: true,
    menuItem: false,
    path: "/uk-assessment",
    icon: MarketsIcon,
    component: UKPublicAssessment,
  },
  {
    title: "Entity Onboarding",
    menuItem: false,
    path: "/entity-onboarding",
    icon: BusinessCompanyIcon,
    component: Sumsub,
    permissions: ["user", "admin", "developer", "beta", "guest"],
  },
  {
    title: "Staking",
    menuItem: true,
    path: "/staking",
    icon: BusinessCompanyIcon,
    component: Staking,
    featurePermission: ["staking"],
  },
  {
    title: "Application",
    menuItem: true,
    path: "/application",
    icon: () => <AccountsIcon fontSize="large" style={{ color: "white" }} />,
    component: Application,
    permissions: ["guest"],
  },
  {
    title: "Application",
    menuItem: false,
    path: "/additional-application",
    icon: () => <AccountsIcon fontSize="large" style={{ color: "white" }} />,
    component: Application,
    permissions: ["user", "admin", "developer", "beta"],
  },
  {
    title: "Verify",
    menuItem: false,
    path: "/reKycApplication",
    icon: () => <HttpsOutlinedIcon style={{ fontSize: "20px" }} />,
    component: ReKycApplication,
    permissions: ["user", "admin", "beta"],
  },
  {
    title: "Not found",
    menuItem: false,
    path: "*",
    icon: MarketsIcon,
    component: Portfolio,
  },
  {
    // Route to the login page when the user goes to an unrendered route when not logged in
    title: "verifyemail",
    path: "/verifyemail",
    menuItem: false,
    component: VerifyEmail,
    icon: DashboardIcon,
    public: true,
  },
  {
    // Route to the login page when the user goes to an unrendered route when not logged in
    title: "login",
    path: "*",
    menuItem: false,
    component: LandingPage,
    icon: DashboardIcon,
    public: true,
  },
];

export const allRoutes: RouteItem[] = [...mainRoutes];
